import { NUMBER_OF_ROUNDS, Tuple } from "./types/GameTypes";

export type Stat = {
  gameNumber: number;
  score: number;
}

const asc = (sa: Stat, sb: Stat):number => sa.gameNumber - sb.gameNumber;
const desc = (sa: Stat, sb: Stat):number => sb.gameNumber - sa.gameNumber;

type StatLambda = (s: Stat) => boolean;


const untilNow = (mostRecentGameNumber: number):StatLambda => {
  const lambda = (s: Stat):boolean => s.gameNumber <= mostRecentGameNumber;
  return lambda;
};

const isWin = (stat: Stat):boolean => {
  return stat.score < 6;
}

export const winPercentage = (stats:Stat[]):number => {
  const total = stats.length;
  const wins = stats.filter(isWin);
  return Math.floor(wins.length * 100 / total); 
}

type StreakRecord = {
  bestStreakLength: number
  lastGameNumber: number
  currentStreakLength: number
}

export const bestStreak = (stats:Stat[], mostRecentGameNumber: number):number => {
  const allWins = stats.filter(isWin).filter(untilNow(mostRecentGameNumber)).sort(asc);
  if (allWins.length === 0) return 1;

  const record:StreakRecord = allWins.reduce<StreakRecord>((accum: StreakRecord, win:Stat) => {
    const { bestStreakLength, lastGameNumber, currentStreakLength} = accum;
    
    if (bestStreakLength === 0) {
      // First win is automatically the reference
      return {
        bestStreakLength: 1,
        lastGameNumber: win.gameNumber,
        currentStreakLength: 1
      };
    } else {
      if (win.gameNumber === lastGameNumber + 1) {
        const newCurrentStreakLength = currentStreakLength + 1;
        return {
          bestStreakLength: (newCurrentStreakLength > bestStreakLength) ? newCurrentStreakLength : bestStreakLength,
          lastGameNumber: win.gameNumber,
          currentStreakLength: newCurrentStreakLength
        };
      } else {
        // streak is broken
        return {
          bestStreakLength,
          lastGameNumber: win.gameNumber,
          currentStreakLength: 1
        }
      }
    }
  }, {
    bestStreakLength: 0,
    lastGameNumber: 0,
    currentStreakLength: 0
  });
  return record.bestStreakLength;
}

export const currentStreak = (stats:Stat[], mostRecentGameNumber: number):number => {
  const allWins = stats.filter(isWin).filter(untilNow(mostRecentGameNumber)).sort(desc);
  
  let streakStart = mostRecentGameNumber;
  for (const stat of allWins) {
    if (stat.gameNumber === streakStart) {
      streakStart = streakStart -1;
    } else {
      break;
    }
  }
  return (mostRecentGameNumber - streakStart);
}

export type BreakdownCategory = {
  value: number,
  label: string,
  frequency: number
}
type _BreakdownCategoryArray<N extends number> = Tuple<BreakdownCategory, N>

const NUMBER_OF_LABELS = NUMBER_OF_ROUNDS + 1; // +1 for "LOST"

export type BreakdownCategoryArray = _BreakdownCategoryArray<typeof NUMBER_OF_LABELS>

export type StatsBreakdown = {
  categories: BreakdownCategoryArray,
  maxFrequency: number
}

export const breakdown = (stats:Stat[]):StatsBreakdown => {

  const buckets:BreakdownCategory[] = [];

  for (let i = 0; i < NUMBER_OF_LABELS; i++) {
    buckets.push({
      value: i === 0 ? 6 : i,
      label: i === 0 ? 'L' : `${i}`,
      frequency: 0
    })
  };

  stats.forEach( stat => {
   const targetBucket = buckets.find(b => b.value === stat.score);
    if (targetBucket) {
      targetBucket.frequency = targetBucket.frequency + 1
    }
  })

  const maxFrequency = buckets.reduce<number>((max: number, bc: BreakdownCategory) => {
    if (bc.frequency > max) {
      return bc.frequency;
    }
    return max;
  }, 0);

  return {
    categories: buckets.sort((ba, bc) => ba.value - bc.value),
    maxFrequency
  }
}
